import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectTopLevelOrganizationApproverError } from 'src/app/BusinessRules/redux/businessRules.selectors'
import { selectPeopleMembers } from 'src/app/OrganizationInfo/redux/organizationInfo.selectors'
import { getOrgMember } from 'src/app/PeopleDetailFlyout/redux/peopleDetailFlyout.actions'
import { getOrgMembers } from 'src/app/PeopleMembers/redux/peopleMembers.actions'
import Box from 'src/components/Box/Box'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import OutOfPocketExpensesBoxContent from 'src/components/OrgSettingsOutOfPocketExpensesBox/OrgSettingsOutOfPocketExpensesBoxContent'
import OutOfPocketExpensesForm from 'src/components/OrgSettingsOutOfPocketExpensesBox/OrgSettingsOutOfPocketExpensesForm'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import { useBackgroundDispatch } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { OrganizationsListModel } from 'src/models/organizations.models'
import { UserListModel } from 'src/models/user.models'
import { css } from 'styled-components'

interface OutOfPocketExpensesBoxProps {
  user?: UserListModel
  organization: OrganizationsListModel
}

const OutOfPocketExpensesBox: React.FC<OutOfPocketExpensesBoxProps> = (
  props,
): React.ReactElement => {
  const dispatch = useBackgroundDispatch()
  const membersFullPaginatedData = useSelector(selectPeopleMembers)
  const topLevelError = useSelector(selectTopLevelOrganizationApproverError)
  const { user, organization } = props

  const [
    isOutOfPocketExpensesBeingEdited,
    setIsOutOfPocketExpensesBeingEdited,
  ] = useState<boolean>(false)

  const fetchOrgMembers = (filterInput?: string, page?: number): void => {
    dispatch(
      getOrgMembers(organization.id, {
        page: page || 0,
        count: 10,
        search: filterInput,
        renderMetrics: false,
      }),
    )
  }
  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchOrgMembers,
    ...membersFullPaginatedData,
  }

  useEffect(() => {
    if (organization.expenseApprover !== undefined) {
      // to load current approver (required to avoid user id flashing in place of the user's name/email)
      dispatch(getOrgMember(organization.expenseApprover.id))
    }
  }, [organization.expenseApprover])

  return (
    <Box className={'flex-column'} noShadow outlineBorder>
      <div
        css={css`
          margin-bottom: 16px;
          display: flex;
          justify-content: space-between;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 10px;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                20,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            <Trns source={'Out-of-Pocket Expenses'} />
          </span>
        </div>
        {user?.hasOrgAdminPermissions && !isOutOfPocketExpensesBeingEdited && (
          <HoverableLink
            onClick={(): void => {
              setIsOutOfPocketExpensesBeingEdited(true)
            }}
            ariaLabelSource={'Update'}
            css={css`
              display: flex;
              align-items: center;
            `}
            testId={'updateOopeButton'}
          >
            <Trns source={'Update'} />
            <SvgUpdate
              css={css`
                transform: scale(0.7);
                margin-left: 4px;
              `}
            />
          </HoverableLink>
        )}
      </div>
      {isOutOfPocketExpensesBeingEdited ? (
        <OutOfPocketExpensesForm
          userInfiniteScrollProps={userInfiniteScrollProps}
          closeOutOfPocketExpensesForm={(): void => {
            setIsOutOfPocketExpensesBeingEdited(false)
          }}
          organization={organization}
          approver={organization.expenseApprover}
          error={topLevelError}
        />
      ) : (
        <OutOfPocketExpensesBoxContent organization={organization} />
      )}
    </Box>
  )
}

export default OutOfPocketExpensesBox
